import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { useCountUp } from 'use-count-up'
import { isFinite } from 'lodash'
import { Row, Col, ColProps } from '../Grid'
import { Typography } from '../AppTypography'
import { Tooltip } from '../Tooltip'
import { CommuteScoreProgress } from '../Progress/CommuteScoreProgress'
import { colors } from '../style/constants'
import { Skeleton } from '../Skeleton'
import { TypedIntl, useLocaleReceiver } from '../locales/LocaleReceiver'
import { ScoreVariationTag } from './ScoreVariationTag'
import './styles.scss'

const commuteScoreCols: Partial<ColProps> = { xs: 24, sm: 19 }
const scoreFeedbackCols: Partial<ColProps> = { xs: 24, sm: 5 }

const PERCENT_ANIMATION_DURATION = 1.5

export const mapScoreToFeedback = (score: number, intl: TypedIntl<'CommuteScore'>) => {
  const roundedScore = Math.round(score)
  switch (true) {
    case roundedScore < 35:
      return intl.formatMessage({ id: 'lowGrade' })
    case roundedScore >= 35 && roundedScore < 50:
      return intl.formatMessage({ id: 'averageGrade' })
    case roundedScore >= 50 && roundedScore < 60:
      return intl.formatMessage({ id: 'aboveAverageGrade' })
    case roundedScore >= 60 && roundedScore < 75:
      return intl.formatMessage({ id: 'goodGrade' })
    case roundedScore >= 75 && roundedScore < 90:
      return intl.formatMessage({ id: 'greatGrade' })
    case roundedScore >= 90 && roundedScore <= 99:
      return intl.formatMessage({ id: 'almostPerfectGrade' })
    case roundedScore === 100:
      return intl.formatMessage({ id: 'perfectGrade' })
    default:
      return intl.formatMessage({ id: 'error' })
  }
}

const getFeedbackColor = (score: number | undefined) => {
  const roundedScore = Math.round(score ?? 0)
  switch (true) {
    case roundedScore < 35:
      return colors.feedbackDestructive600
    case roundedScore >= 35 && roundedScore < 50:
      return colors.neutral500
    case roundedScore >= 50 && roundedScore <= 100:
      return colors.feedbackSuccess600
    default:
      return colors.neutral500
  }
}

export function CommuteScore({
  score,
  scoreVariationPercentage,
  isLoading = false
}: {
  score: number | undefined
  scoreVariationPercentage?: number
  isLoading?: boolean
}) {
  const intl = useLocaleReceiver('CommuteScore')
  const boundedScore = score && score > 100 ? 100 : score && score < 0 ? 0 : score
  const rounded = boundedScore ? Math.round(boundedScore) : boundedScore

  const { value: percentCountUp } = useCountUp({
    isCounting: Boolean(rounded && !isLoading),
    start: 0,
    end: rounded,
    duration: PERCENT_ANIMATION_DURATION
  })
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- It works
  const currentPercent = percentCountUp ? parseInt(percentCountUp.toString()) : 0

  return (
    <Row wrap style={{ position: 'relative', width: '100%' }} className="u-flex-1">
      <Col {...commuteScoreCols}>
        <span className="u-flex-align-center u-flex-wrap u-margin-bottom--small">
          <Typography.Title level={3} variant="h4" style={{ display: 'inline-flex', margin: 0, whiteSpace: 'nowrap' }}>
            {intl.formatMessage({ id: 'title' })}
          </Typography.Title>
          <Tooltip title={intl.formatMessage({ id: 'presentHow' })} overlayStyle={{ maxWidth: '350px' }}>
            <FontAwesomeIcon icon={faInfoCircle} size="lg" className="u-margin-left--tiny-2 u-margin-right--small-3" />
          </Tooltip>

          {isFinite(scoreVariationPercentage) ? (
            <ScoreVariationTag variationPercentage={scoreVariationPercentage ?? 0} />
          ) : null}
        </span>
      </Col>
      {/* Make sure the score text section is relative to the Row component and not this col component bellow */}
      <Col {...scoreFeedbackCols} style={{ position: 'unset' }}>
        <div className="app-commute-score">
          {isLoading ? (
            <Skeleton.Button active size="small" style={{ minWidth: 'unset', width: '40px' }} />
          ) : (
            <Typography.Text strong="black" data-testid="commute-score">
              {currentPercent}
            </Typography.Text>
          )}
          <Typography.Text strong="bold" style={{ color: getFeedbackColor(score) }}>
            {isLoading ? (
              <Skeleton active title={false} paragraph={{ rows: 1, width: 75 }} />
            ) : (
              <span data-testid="commute-score-feedback">{mapScoreToFeedback(boundedScore ?? 0, intl)}</span>
            )}
          </Typography.Text>
        </div>
      </Col>
      <Col {...commuteScoreCols}>
        <CommuteScoreProgress percent={currentPercent} isLoading={isLoading} />
      </Col>
    </Row>
  )
}
